import React from "react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, XCircle, RefreshCw, Zap } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";

const Test = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<{
    [key: string]: { success: boolean; message: string };
  }>({});
  const [ideaId, setIdeaId] = useState("");

  // Only allow access for authenticated users (you can add admin check if needed)
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Access Restricted</CardTitle>
            <CardDescription>Please sign in to access testing tools.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const runTest = async (testName: string, testFn: () => Promise<void>) => {
    setLoading(testName);
    try {
      await testFn();
      setTestResults(prev => ({
        ...prev,
        [testName]: { success: true, message: "Test passed successfully" }
      }));
      toast({
        title: "Test Passed",
        description: `${testName} completed successfully`,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Test failed";
      setTestResults(prev => ({
        ...prev,
        [testName]: { success: false, message: errorMessage }
      }));
      toast({
        title: "Test Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const testGenerateIdea = async () => {
    await runTest("Generate Idea Report", async () => {
      if (!ideaId) throw new Error("Please enter an Idea ID");
      
      const { data, error } = await supabase.functions.invoke('generate-idea-report', {
        body: { ideaId }
      });
      
      if (error) throw error;
      if (!data) throw new Error("No data returned from function");
    });
  };

  const testDataRefresh = async () => {
    await runTest("Refresh Idea Data", async () => {
      if (!ideaId) throw new Error("Please enter an Idea ID");
      
      const { data, error } = await supabase
        .from('ideas')
        .select('*')
        .eq('id', ideaId)
        .single();
      
      if (error) throw error;
      if (!data) throw new Error("Idea not found");
    });
  };

  const testDatabaseConnection = async () => {
    await runTest("Database Connection", async () => {
      const { data, error } = await supabase
        .from('ideas')
        .select('count')
        .limit(1);
      
      if (error) throw error;
    });
  };

  const testAuthStatus = async () => {
    await runTest("Auth Status", async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!session) throw new Error("No active session");
    });
  };

  const testUserIdeas = async () => {
    await runTest("User Ideas Query", async () => {
      const { data, error } = await supabase
        .from('user_ideas')
        .select('*')
        .limit(5);
      
      if (error) throw error;
    });
  };

  const testRecommendations = async () => {
    await runTest("Recommendation Engine", async () => {
      const { data, error } = await supabase
        .rpc('get_recommended_ideas', { p_user_id: user.id });
      
      if (error) throw error;
    });
  };

  const TestResult = ({ testName }: { testName: string }) => {
    const result = testResults[testName];
    if (!result) return null;

    return (
      <div className={`mt-2 p-3 rounded-lg border ${
        result.success 
          ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' 
          : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
      }`}>
        <div className="flex items-start gap-2">
          {result.success ? (
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              result.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'
            }`}>
              {result.message}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEOHead 
        title="Testing Dashboard"
        description="DayOne testing and admin tools"
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-2">
              <h1 className="text-4xl font-serif font-bold">Testing Dashboard</h1>
              <p className="text-muted-foreground">
                Manual testing tools for core DayOne functionality
              </p>
            </div>

            {/* System Tests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  System Tests
                </CardTitle>
                <CardDescription>
                  Verify core system functionality and connections
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    onClick={testDatabaseConnection}
                    disabled={loading === "Database Connection"}
                    variant="outline"
                  >
                    {loading === "Database Connection" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Test Database
                  </Button>
                  <Button
                    onClick={testAuthStatus}
                    disabled={loading === "Auth Status"}
                    variant="outline"
                  >
                    {loading === "Auth Status" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Test Auth
                  </Button>
                  <Button
                    onClick={testUserIdeas}
                    disabled={loading === "User Ideas Query"}
                    variant="outline"
                  >
                    {loading === "User Ideas Query" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Test User Ideas
                  </Button>
                  <Button
                    onClick={testRecommendations}
                    disabled={loading === "Recommendation Engine"}
                    variant="outline"
                  >
                    {loading === "Recommendation Engine" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Test Recommendations
                  </Button>
                </div>

                {Object.keys(testResults).filter(key => 
                  ["Database Connection", "Auth Status", "User Ideas Query", "Recommendation Engine"].includes(key)
                ).map(testName => (
                  <TestResult key={testName} testName={testName} />
                ))}
              </CardContent>
            </Card>

            {/* Idea-Specific Tests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-primary" />
                  Idea Operations
                </CardTitle>
                <CardDescription>
                  Test idea-specific functions and data refresh
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ideaId">Idea ID</Label>
                  <Input
                    id="ideaId"
                    placeholder="Enter idea UUID"
                    value={ideaId}
                    onChange={(e) => setIdeaId(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    onClick={testGenerateIdea}
                    disabled={loading === "Generate Idea Report" || !ideaId}
                    variant="default"
                  >
                    {loading === "Generate Idea Report" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Generate Report
                  </Button>
                  <Button
                    onClick={testDataRefresh}
                    disabled={loading === "Refresh Idea Data" || !ideaId}
                    variant="default"
                  >
                    {loading === "Refresh Idea Data" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Refresh Data
                  </Button>
                </div>

                {Object.keys(testResults).filter(key => 
                  ["Generate Idea Report", "Refresh Idea Data"].includes(key)
                ).map(testName => (
                  <TestResult key={testName} testName={testName} />
                ))}
              </CardContent>
            </Card>

            {/* User Journey Checklist */}
            <Card>
              <CardHeader>
                <CardTitle>Manual Test Checklist</CardTitle>
                <CardDescription>
                  Complete user journey verification steps
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">1.</span>
                    <span>Landing → click "Start Exploring" → see idea cards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">2.</span>
                    <span>Click a card → open full idea detail</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">3.</span>
                    <span>Verify every section loads data (Problem → Execution Plan)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">4.</span>
                    <span>Confirm charts render properly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">5.</span>
                    <span>Test Save / Building / Not Interested buttons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">6.</span>
                    <span>Check "For You" feed populates personalized results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">7.</span>
                    <span>Confirm "Ask AI" chat box loads and responds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground">8.</span>
                    <span>Refresh to ensure state persists</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    </>
  );
};

export default Test;
